import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material'
import { ExpandMore, ChevronRight, WarningAmber } from '@mui/icons-material'
import { LightspeedIcon, XeroIcon } from '../../../../components/SystemIcons'
import { formatCurrency as formatCurrencyFull, formatPercentage } from '../../../../utils/inspectorFormatters'
import { poolwerxColors } from '../../../../theme/colors'

const formatCurrency = (amount: number) =>
  formatCurrencyFull(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })

interface CogsComparisonData {
  month: string
  lightspeed_service_cogs: number
  lightspeed_retail_cogs: number
  lightspeed_total_cogs: number
  xero_closures_cogs: number
  xero_journals_cogs: number
  xero_total_cogs: number
  variance: number
  variance_percentage: number
  cross_month_cogs: number
}

interface ManualJournalDetail {
  date: string
  journal_number: string | null
  narration: string | null
  description: string | null
  amount: number
  account_code: string
  account_name?: string | null
}

interface COGSAccountTableProps {
  data: CogsComparisonData[]
  fpCode?: string
  isUSRegion?: boolean
  loading?: boolean
  manualJournalDetails?: Record<string, ManualJournalDetail[]>
  otherCogsJournalDetails?: Record<string, ManualJournalDetail[]>
}

const JournalEntryRow: React.FC<{ journal: ManualJournalDetail; isLast: boolean }> = ({ journal, isLast }) => {
  const [showDescription, setShowDescription] = useState(false)
  const isMisposted = journal.account_code === '5000-100'

  return (
    <Box sx={{ mb: 1, pb: 1, borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.2)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" component="span">
          <strong>{journal.date}</strong>
        </Typography>
        <Typography variant="caption" component="span">
          {formatCurrency(journal.amount)}
        </Typography>
        <Typography variant="caption" component="span" sx={{ opacity: 0.9 }}>
          {journal.account_code}
        </Typography>
        {isMisposted && (
          <Chip
            size="small"
            color="warning"
            label="Wrong account - expected 5000-200"
            sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
          />
        )}
      </Box>
      {journal.narration && (
        <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
          {journal.narration}
        </Typography>
      )}
      {journal.description && (
        <Box sx={{ mt: 0.25 }}>
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              setShowDescription((s) => !s)
            }}
            sx={{
              cursor: 'pointer',
              fontSize: '0.7rem',
              textDecoration: 'underline',
              opacity: 0.85,
              userSelect: 'none',
            }}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </Box>
          {showDescription && (
            <Typography variant="caption" display="block" sx={{ mt: 0.25, opacity: 0.85 }}>
              {journal.description}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

interface TableRowData {
  id: string
  label: string
  caption?: string
  icon?: React.ReactNode
  isSystemHeader?: boolean
  isSubRow?: boolean
  isTotalRow?: boolean
  isVarianceRow?: boolean
  systemType?: 'lightspeed' | 'xero'
  isClickable?: boolean
  values: Record<string, number>
  totalValue?: number
}

const COGSAccountTable: React.FC<COGSAccountTableProps> = ({
  data,
  fpCode,
  isUSRegion = false,
  loading = false,
  manualJournalDetails = {},
  otherCogsJournalDetails = {}
}) => {
  // State for tracking which systems are expanded
  const [expandedSystems, setExpandedSystems] = useState<{
    lightspeed: boolean
    xero: boolean
  }>({
    lightspeed: false, // Start collapsed
    xero: false       // Start collapsed
  })

  // Toggle function for expanding/collapsing systems
  const toggleSystem = (system: 'lightspeed' | 'xero') => {
    setExpandedSystems(prev => ({
      ...prev,
      [system]: !prev[system]
    }))
  }

  if (loading || !data || data.length === 0) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>System/Source</TableCell>
              <TableCell align="center">No data available</TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
    )
  }

  // Format month for display
  const formatMonthDisplay = (monthStr: string) => {
    const [year, month] = monthStr.split('-')
    const date = new Date(parseInt(year), parseInt(month) - 1)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
  }

  // Build data explorer link for cross-month sales in a given month
  const buildCrossMonthLink = (monthStr: string) => {
    if (!fpCode) return null
    const [year, month] = monthStr.split('-')
    const startDate = `${year}-${month}-01`
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate()
    const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`
    return `/inspect/${fpCode}/data/sales?start_date=${startDate}&end_date=${endDate}&cross_month_only=true`
  }

  // Calculate totals for each column
  const calculateTotals = (key: keyof CogsComparisonData) => {
    return data.reduce((sum, item) => sum + (item[key] as number), 0)
  }

  // Prepare table rows
  const prepareTableRows = (): TableRowData[] => {
    const rows: TableRowData[] = []

    // Xero section (now first)
    rows.push({
      id: 'xero-header',
      label: 'Xero',
      icon: <XeroIcon size={20} />,
      isSystemHeader: true,
      systemType: 'xero',
      isClickable: true,
      values: data.reduce((acc, item) => {
        acc[item.month] = item.xero_total_cogs
        return acc
      }, {} as Record<string, number>),
      totalValue: calculateTotals('xero_total_cogs')
    })

    // Xero sub-rows (only if expanded)
    if (expandedSystems.xero) {
      // Lightspeed register closures (auto-posted ACCPAY bills on 5000-100)
      rows.push({
        id: 'xero-closures',
        label: 'LS Register Closures',
        caption: '5000-100',
        isSubRow: true,
        values: data.reduce((acc, item) => {
          acc[item.month] = item.xero_closures_cogs
          return acc
        }, {} as Record<string, number>),
        totalValue: calculateTotals('xero_closures_cogs')
      })

      // Monthly COGS Adjustments (manual journals on 5000-100 and 5000-200)
      rows.push({
        id: 'xero-journals',
        label: 'Monthly COGS Adjustments (manual journals)',
        caption: '5000-100 / 5000-200',
        isSubRow: true,
        values: data.reduce((acc, item) => {
          acc[item.month] = item.xero_journals_cogs
          return acc
        }, {} as Record<string, number>),
        totalValue: calculateTotals('xero_journals_cogs')
      })
    }

    // Lightspeed section (now second)
    rows.push({
      id: 'lightspeed-header',
      label: 'Lightspeed',
      icon: <LightspeedIcon size={20} />,
      isSystemHeader: true,
      systemType: 'lightspeed',
      isClickable: true,
      values: data.reduce((acc, item) => {
        acc[item.month] = item.lightspeed_total_cogs
        return acc
      }, {} as Record<string, number>),
      totalValue: calculateTotals('lightspeed_total_cogs')
    })

    // Lightspeed sub-rows (only if expanded)
    if (expandedSystems.lightspeed) {
      // Service row (for US regions - Skimmer) or (for AU/NZ regions - Pooltrackr)
      const serviceLabel = isUSRegion ? 'Service (Skimmer)' : 'Service (Pooltrackr)'
      rows.push({
        id: 'lightspeed-service',
        label: serviceLabel,
        isSubRow: true,
        values: data.reduce((acc, item) => {
          acc[item.month] = item.lightspeed_service_cogs
          return acc
        }, {} as Record<string, number>),
        totalValue: calculateTotals('lightspeed_service_cogs')
      })

      // Retail row
      rows.push({
        id: 'lightspeed-retail',
        label: 'Retail',
        isSubRow: true,
        values: data.reduce((acc, item) => {
          acc[item.month] = item.lightspeed_retail_cogs
          return acc
        }, {} as Record<string, number>),
        totalValue: calculateTotals('lightspeed_retail_cogs')
      })
    }

    // Variance section
    rows.push({
      id: 'variance',
      label: 'Variance',
      isVarianceRow: true,
      values: data.reduce((acc, item) => {
        acc[item.month] = item.variance
        return acc
      }, {} as Record<string, number>),
      totalValue: calculateTotals('variance')
    })

    return rows
  }

  const rows = prepareTableRows()
  const totalVariancePercentage = calculateTotals('xero_total_cogs') !== 0 
    ? (calculateTotals('variance') / calculateTotals('xero_total_cogs')) * 100 
    : 0

  return (
    <TableContainer 
      component={Paper}
      sx={{
        overflowX: 'auto',
        position: 'relative',
        maxWidth: '100%',
        '& .MuiTable-root': {
          borderCollapse: 'separate',
          borderSpacing: 0
        }
      }}
    >
      <Table size="medium" sx={{ minWidth: '800px' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: poolwerxColors.teal }}>
            <TableCell sx={{
              fontWeight: 'bold',
              minWidth: '250px',
              width: '250px',
              color: 'white',
              fontSize: '1rem',
              position: 'sticky',
              left: 0,
              zIndex: 3,
              bgcolor: poolwerxColors.teal,
              borderRight: '2px solid rgba(255, 255, 255, 0.3)'
            }}>
              System/Source
            </TableCell>
            {data.map(item => (
              <TableCell key={item.month} align="right" sx={{ 
                fontWeight: 'bold', 
                minWidth: '120px',
                color: 'white',
                fontSize: '1rem',
                borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                {formatMonthDisplay(item.month)}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ 
              fontWeight: 'bold', 
              minWidth: '120px',
              color: 'white',
              fontSize: '1rem',
              borderLeft: '2px solid rgba(255, 255, 255, 0.4)'
            }}>
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.id}
              sx={{
                cursor: row.isClickable ? 'pointer' : 'default',
                '&:hover': row.isClickable ? {
                  bgcolor: row.isSystemHeader ? '#e8e8e8' : undefined,
                  '& td': {
                    bgcolor: row.isSystemHeader ? '#e8e8e8' : undefined
                  },
                  '& td:first-of-type': {
                    bgcolor: row.isSystemHeader ? '#e8e8e8' :
                             row.isVarianceRow ? '#ffe0b3' :
                             '#f5f5f5' // Light background on hover for non-header rows
                  }
                } : undefined
              }}
              onClick={row.isClickable && row.systemType ? () => toggleSystem(row.systemType!) : undefined}
            >
              <TableCell
                sx={{
                  fontWeight: row.isSystemHeader || row.isTotalRow || row.isVarianceRow ? 'bold' : 'normal',
                  pl: row.isSubRow ? 4 : row.isSystemHeader || row.isVarianceRow ? 1 : 2,
                  borderTop: row.isSystemHeader || row.isVarianceRow ? `2px solid #bbb` : undefined,
                  bgcolor: row.isSystemHeader ? '#f5f5f5' :
                           row.isVarianceRow ? '#fff3e0' :
                           'white', // Always have an opaque background
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: '250px',
                  width: '250px',
                  borderRight: '1px solid #e0e0e0',
                  isolation: 'isolate' // Create new stacking context
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {row.isClickable && row.systemType && (
                    <Tooltip title={`Click to ${expandedSystems[row.systemType] ? 'collapse' : 'expand'}`}>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          p: 0.5,
                          transition: 'transform 0.2s',
                          transform: expandedSystems[row.systemType] ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.icon}
                  <Box>
                    <Typography
                      variant={row.isSystemHeader || row.isVarianceRow ? 'subtitle1' : 'body2'}
                      sx={{
                        fontWeight: row.isSystemHeader || row.isTotalRow || row.isVarianceRow ? 'bold' : 'normal',
                        color: 'text.primary',
                        lineHeight: 1.2,
                      }}
                    >
                      {row.label}
                    </Typography>
                    {row.caption && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 'normal',
                          display: 'block',
                          lineHeight: 1.2,
                          letterSpacing: 0.3,
                        }}
                      >
                        {row.caption}
                      </Typography>
                    )}
                    {row.isVarianceRow && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 'normal',
                          display: 'block',
                          lineHeight: 1.2
                        }}
                      >
                        Xero vs Lightspeed
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              
              {data.map(item => {
                const value = row.values[item.month] || 0
                const isNegative = value < 0
                
                const monthVariancePercentage = data.find(d => d.month === item.month)?.xero_total_cogs !== 0 
                  ? (value / (data.find(d => d.month === item.month)?.xero_total_cogs || 1)) * 100 
                  : 0
                
                // Check if this is the manual journals row and prepare tooltip
                const isManualJournalsRow = row.id === 'xero-journals'
                const journalDetails = isManualJournalsRow ? manualJournalDetails[item.month] : null
                const otherJournalDetails = isManualJournalsRow ? otherCogsJournalDetails[item.month] : null

                // Group supplementary other-account journals by account_code for tooltip rendering
                const otherJournalsByAccount: Array<{
                  accountCode: string
                  accountName: string | null
                  total: number
                  entries: ManualJournalDetail[]
                }> = (() => {
                  if (!otherJournalDetails || otherJournalDetails.length === 0) return []
                  const grouped = new Map<string, { accountName: string | null; total: number; entries: ManualJournalDetail[] }>()
                  for (const entry of otherJournalDetails) {
                    const existing = grouped.get(entry.account_code)
                    if (existing) {
                      existing.total += entry.amount
                      existing.entries.push(entry)
                    } else {
                      grouped.set(entry.account_code, {
                        accountName: entry.account_name ?? null,
                        total: entry.amount,
                        entries: [entry],
                      })
                    }
                  }
                  return Array.from(grouped.entries())
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([accountCode, group]) => ({ accountCode, ...group }))
                })()

                // Cross-month COGS for variance annotation
                const crossMonthValue = row.isVarianceRow ? (item.cross_month_cogs || 0) : 0

                // Per-month adjustment-row signals: drives both the inline
                // warning icon and the tooltip's lead paragraph.
                const monthHasMisposting = !!(
                  journalDetails && journalDetails.some((j) => j.account_code === '5000-100')
                )
                const monthHasOther = otherJournalsByAccount.length > 0
                const showCellWarningIcon =
                  isManualJournalsRow && (monthHasMisposting || monthHasOther)

                const cellContent = (
                  <>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      {showCellWarningIcon && (
                        <WarningAmber sx={{ fontSize: '0.95rem', color: 'warning.main' }} />
                      )}
                      <span style={{
                        color: (isManualJournalsRow && isNegative) ? poolwerxColors.error : undefined
                      }}>
                        {formatCurrency(value)}
                      </span>
                    </Box>
                    {row.isVarianceRow && (
                      <div style={{
                        fontSize: '0.8em',
                        color: Math.abs(monthVariancePercentage) < 0.01 ? poolwerxColors.success :
                               monthVariancePercentage > 0 ? '#ff9800' : poolwerxColors.error,
                        fontWeight: 'bold'
                      }}>
                        ({monthVariancePercentage >= 0 ? '+' : ''}{formatPercentage(monthVariancePercentage)})
                      </div>
                    )}
                    {row.isVarianceRow && crossMonthValue > 0.01 && (() => {
                      const crossMonthLink = buildCrossMonthLink(item.month)
                      const noteContent = (
                        <div style={{
                          fontSize: '0.7em',
                          color: '#6d4c00',
                          cursor: crossMonthLink ? 'pointer' : 'help',
                          marginTop: '12px',
                          padding: '2px 5px',
                          backgroundColor: '#fff8e1',
                          border: '1px solid #ffe082',
                          borderRadius: '3px',
                          display: 'inline-block'
                        }}>
                          <div style={{ lineHeight: 1.2 }}>Cross-month:</div>
                          <div style={{ fontWeight: 'bold', lineHeight: 1.2 }}>{formatCurrency(crossMonthValue)}</div>
                        </div>
                      )
                      return (
                        <Tooltip title="Lightspeed sales dated this month but created in a different month. The COGS for these sales is likely posted to Xero under the month they were created, not the sale date month. Click to view these sales." placement="top" arrow>
                          {crossMonthLink ? (
                            <Link to={crossMonthLink} style={{ textDecoration: 'none' }}>
                              {noteContent}
                            </Link>
                          ) : noteContent}
                        </Tooltip>
                      )
                    })()}
                  </>
                )
                
                // Build tooltip content for manual journals
                const hasPrimary = !!(journalDetails && journalDetails.length > 0)
                const hasOther = otherJournalsByAccount.length > 0
                const tooltipContent = (hasPrimary || hasOther) ? (
                  <Box sx={{ maxWidth: 460 }}>
                    {monthHasMisposting && (
                      <Box sx={{ mb: 1, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.3)' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.light' }}
                        >
                          <WarningAmber sx={{ fontSize: '1rem' }} />
                          Some COGS adjustments were posted to 5000-100
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.95 }}>
                          FPs should post monthly COGS adjustments to 5000-200. We have included these 5000-100 journals in the total so the variance is correct, but they should be reviewed and posted to the correct account next time.
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                      {hasPrimary && (
                        <>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Adjustments
                          </Typography>
                          {journalDetails!.map((journal, idx) => (
                            <JournalEntryRow
                              key={idx}
                              journal={journal}
                              isLast={idx === journalDetails!.length - 1}
                            />
                          ))}
                        </>
                      )}
                      {hasOther && (
                        <Box sx={{ mt: hasPrimary ? 2 : 0, pt: hasPrimary ? 1 : 0, borderTop: hasPrimary ? '1px solid rgba(255,255,255,0.3)' : 'none' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WarningAmber sx={{ fontSize: '1rem', color: 'warning.light' }} />
                            Journals on other COGS accounts
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 1, fontStyle: 'italic', opacity: 0.9 }}>
                            These are posted to a different COGS account, so they're not included in the row total or the variance.
                          </Typography>
                          {otherJournalsByAccount.map((group, gIdx) => (
                            <Box key={group.accountCode} sx={{ mb: 1, pb: 1, borderBottom: gIdx < otherJournalsByAccount.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none' }}>
                              <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                {group.accountCode}
                                {group.accountName ? ` – ${group.accountName}` : ''}
                                {' · '}
                                total: {formatCurrency(group.total)}
                              </Typography>
                              {group.entries.map((journal, idx) => (
                                <Box key={idx} sx={{ pl: 1, mt: 0.5 }}>
                                  <Typography variant="caption" display="block">
                                    {journal.date}
                                    {journal.journal_number ? ` · ${journal.journal_number}` : ''}
                                    {' · '}
                                    {formatCurrency(journal.amount)}
                                  </Typography>
                                  {journal.narration && (
                                    <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                                      {journal.narration}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : null
                
                return (
                  <TableCell
                    key={item.month}
                    align="right"
                    sx={{
                      fontWeight: row.isSystemHeader || row.isTotalRow || row.isVarianceRow ? 'bold' : 'normal',
                      color: row.isVarianceRow ? (Math.abs(value) < 0.01 ? 'text.primary' : value > 0 ? 'warning.main' : 'error.main') : 'text.primary',
                      borderTop: row.isSystemHeader || row.isVarianceRow ? `2px solid #bbb` : undefined,
                      bgcolor: row.isSystemHeader ? '#f5f5f5' : row.isVarianceRow ? '#fff3e0' : undefined,
                      borderLeft: '1px solid rgba(224, 224, 224, 0.5)'
                    }}
                  >
                    {isManualJournalsRow && tooltipContent ? (
                      <Tooltip title={tooltipContent} placement="top" arrow>
                        <Box sx={{ cursor: 'help' }}>
                          {cellContent}
                        </Box>
                      </Tooltip>
                    ) : (
                      cellContent
                    )}
                  </TableCell>
                )
              })}
              
              {/* Total column */}
              <TableCell
                align="right"
                sx={{
                  fontWeight: row.isSystemHeader || row.isTotalRow || row.isVarianceRow ? 'bold' : 'normal',
                  color: row.isVarianceRow && row.totalValue !== undefined ?
                         (Math.abs(row.totalValue) < 0.01 ? 'text.primary' : row.totalValue > 0 ? 'warning.main' : 'error.main') :
                         (row.id === 'xero-journals' && row.totalValue && row.totalValue < 0) ? 'error.main' : 'text.primary',
                  borderTop: row.isSystemHeader || row.isVarianceRow ? `2px solid #bbb` : undefined,
                  bgcolor: row.isSystemHeader ? '#f5f5f5' : row.isVarianceRow ? '#fff3e0' : undefined,
                  borderLeft: '2px solid #e0e0e0'
                }}
              >
                {formatCurrency(row.totalValue || 0)}
                {row.isVarianceRow && row.totalValue !== undefined && (
                  <div style={{
                    fontSize: '0.9em',
                    color: Math.abs(totalVariancePercentage) < 0.01 ? poolwerxColors.success :
                           totalVariancePercentage > 0 ? '#ff9800' : poolwerxColors.error,
                    fontWeight: 'bold',
                    marginTop: '2px'
                  }}>
                    ({totalVariancePercentage >= 0 ? '+' : ''}{formatPercentage(totalVariancePercentage)})
                  </div>
                )}
                {row.isVarianceRow && (() => {
                  const totalCrossMonth = data.reduce((sum, item) => sum + (item.cross_month_cogs || 0), 0)
                  if (totalCrossMonth <= 0.01) return null
                  // Build link spanning the full date range
                  const sortedMonths = data.map(d => d.month).sort()
                  const totalLink = sortedMonths.length > 0 ? buildCrossMonthLink(sortedMonths[0]) : null
                  const fullRangeLink = totalLink && sortedMonths.length > 1
                    ? (() => {
                        const lastMonth = sortedMonths[sortedMonths.length - 1]
                        const [year, month] = lastMonth.split('-')
                        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate()
                        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`
                        const firstMonth = sortedMonths[0]
                        return `/inspect/${fpCode}/data/sales?start_date=${firstMonth}-01&end_date=${endDate}&cross_month_only=true`
                      })()
                    : totalLink
                  const noteContent = (
                    <div style={{
                      fontSize: '0.7em',
                      color: '#6d4c00',
                      cursor: fullRangeLink ? 'pointer' : 'help',
                      marginTop: '12px',
                      padding: '2px 5px',
                      backgroundColor: '#fff8e1',
                      border: '1px solid #ffe082',
                      borderRadius: '3px',
                      display: 'inline-block'
                    }}>
                      <div style={{ lineHeight: 1.2 }}>Cross-month:</div>
                      <div style={{ fontWeight: 'bold', lineHeight: 1.2 }}>{formatCurrency(totalCrossMonth)}</div>
                    </div>
                  )
                  return (
                    <Tooltip title="Total Lightspeed COGS for sales dated in one month but created in another. These are likely posted to Xero under the month they were created, not the sale date month. Click to view these sales." placement="top" arrow>
                      {fullRangeLink ? (
                        <Link to={fullRangeLink} style={{ textDecoration: 'none' }}>
                          {noteContent}
                        </Link>
                      ) : noteContent}
                    </Tooltip>
                  )
                })()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default COGSAccountTable